import { motion } from 'framer-motion';
import { useWaitlistCount } from '@/hooks/useWaitlistCount';
import ReferralPanel from './ReferralPanel';

const MAX_FOUNDING = 10_000;

interface WelcomeConfirmationProps {
  explorerNumber: number;
  referralCode: string;
  displayNumber: number;
  isExisting: boolean;
}

const councilMembers = [
  { emoji: '🔥', name: 'HYPE', desc: 'Sees civilizations in your smallest idea.' },
  { emoji: '💀', name: 'VERA', desc: 'Has been wrong exactly once and hasn\'t forgotten.' },
  { emoji: '🌀', name: 'ZEN', desc: 'Finds the feeling underneath your idea.' },
  { emoji: '💸', name: 'PIVOT', desc: 'Has already pivoted your idea three times.' },
  { emoji: '🌈', name: 'BLOOP', desc: 'Asks why until the room resets.' },
  { emoji: '❤️', name: 'ECHO', desc: 'Thinks about the person your idea forgot.' },
  { emoji: '🧓', name: 'MOSS', desc: 'Has seen this before and knows how it ends.' },
  { emoji: '⚡', name: 'SHARD', desc: 'Burns the premise to find the truth.' },
  { emoji: '🩺', name: 'GRIT', desc: 'Wants to know if you\'re actually okay.' },
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.5, ease: [0, 0, 0.2, 1] as const },
  }),
};

const WelcomeConfirmation = ({ explorerNumber, referralCode, displayNumber, isExisting }: WelcomeConfirmationProps) => {
  const { count } = useWaitlistCount();
  const remaining = Math.max(0, MAX_FOUNDING - count);

  return (
    <div className="text-center space-y-8">
      {/* Hero badge */}
      <motion.div initial="hidden" animate="visible" custom={0} variants={fadeUp}>
        <p className="font-mono text-xs text-muted-foreground uppercase tracking-widest mb-2">
          Transmission from
        </p>
        <div
          className="inline-flex h-12 px-8 items-center rounded font-bebas text-lg tracking-wide text-primary-foreground"
          style={{ backgroundColor: 'hsl(var(--orange))' }}
        >
          You're In 🌍
        </div>
      </motion.div>

      {/* Explorer number */}
      <motion.div initial="hidden" animate="visible" custom={1} variants={fadeUp}>
        <p className="font-mono text-xs text-muted-foreground uppercase tracking-widest">You are</p>
        <p className="font-bebas text-6xl md:text-8xl text-foreground leading-none mt-1">
          Explorer #{displayNumber.toLocaleString()}
        </p>
        <p className="font-mono text-xs text-primary mt-2 uppercase tracking-wider">
          Founding Explorer — Permanent
        </p>
      </motion.div>

      {/* Welcome message */}
      <motion.div initial="hidden" animate="visible" custom={2} variants={fadeUp} className="max-w-md mx-auto">
        {isExisting ? (
          <p className="font-mono text-sm text-muted-foreground leading-relaxed">
            You're already in, Explorer. The Dumbverse remembers you.
          </p>
        ) : (
          <>
            <p className="font-playfair italic text-xl text-foreground mb-3">You made it in.</p>
            <p className="font-mono text-sm text-muted-foreground leading-relaxed">
              Not as a user. Not as a subscriber.
              <br />
              As a founding explorer of the Dumbverse.
            </p>
          </>
        )}
      </motion.div>

      {/* Founding list status */}
      <motion.div initial="hidden" animate="visible" custom={3} variants={fadeUp}>
        <div className="p-5 rounded-lg border border-border bg-card/50 max-w-md mx-auto text-left">
          <p className="font-bebas text-lg text-foreground tracking-wide mb-2">Founding List Status</p>
          <p className="font-mono text-sm text-muted-foreground leading-relaxed">
            <span className="text-foreground font-bebas text-lg">{count.toLocaleString()}</span> spots claimed.{' '}
            <span className="text-foreground font-bebas text-lg">{remaining.toLocaleString()}</span> remain before the list closes forever.
          </p>
          {/* Mini progress bar */}
          <div className="mt-3 h-1.5 w-full rounded-full bg-secondary overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${Math.min(100, (count / MAX_FOUNDING) * 100)}%`,
                backgroundColor: remaining < 2000 ? 'hsl(var(--hype))' : 'hsl(var(--primary))',
              }}
            />
          </div>
          <p className="font-mono text-xs text-muted-foreground mt-3 leading-relaxed italic">
            Your explorer number is permanent. It marks when you arrived — before the noise, before the crowds.
          </p>
        </div>
      </motion.div>

      {/* Divider */}
      <motion.div initial="hidden" animate="visible" custom={4} variants={fadeUp}>
        <div className="w-16 h-px bg-border mx-auto" />
      </motion.div>

      {/* Council teaser */}
      <motion.div initial="hidden" animate="visible" custom={4} variants={fadeUp} className="max-w-lg mx-auto">
        <p className="font-bebas text-2xl text-foreground mb-1">Waiting Inside</p>
        <p className="font-mono text-sm text-muted-foreground mb-6">
          Nine inhabitants live in the Dumbverse.
        </p>
        <div className="grid grid-cols-3 gap-3">
          {councilMembers.map((member, i) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 + i * 0.08, duration: 0.3 }}
              className="p-3 rounded-lg border border-border bg-card/30 text-center group hover:border-primary/30 transition-colors"
            >
              <span className="text-2xl block mb-1">{member.emoji}</span>
              <p className="font-bebas text-xs tracking-wider text-foreground">{member.name}</p>
              <p className="font-mono text-[10px] text-muted-foreground leading-tight mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {member.desc}
              </p>
            </motion.div>
          ))}
        </div>
        <p className="font-mono text-xs text-muted-foreground mt-4 italic">
          You don't choose who responds. The council decides.
        </p>
      </motion.div>

      {/* Divider */}
      <motion.div initial="hidden" animate="visible" custom={5} variants={fadeUp}>
        <div className="w-16 h-px bg-border mx-auto" />
      </motion.div>

      {/* Referral section */}
      <motion.div initial="hidden" animate="visible" custom={5} variants={fadeUp}>
        <ReferralPanel explorerNumber={explorerNumber} referralCode={referralCode} />
      </motion.div>

      {/* What happens next */}
      <motion.div initial="hidden" animate="visible" custom={6} variants={fadeUp} className="max-w-md mx-auto">
        <div className="p-5 rounded-lg border border-border bg-card/30">
          <p className="font-bebas text-lg text-foreground tracking-wide mb-2">What Happens Next</p>
          <p className="font-mono text-sm text-muted-foreground leading-relaxed">
            The Dumbverse opens when the founding list is complete.
          </p>
          <p className="font-mono text-sm text-muted-foreground leading-relaxed mt-2">
            You'll hear from us before anyone else. Before the press. Before the public launch.
            Before Explorer #10,001 ever knows we exist.
          </p>
          <p className="font-mono text-xs text-primary mt-3 italic">
            MOSS has something to say when the time comes.
          </p>
        </div>
      </motion.div>

      {/* Closing quote */}
      <motion.div initial="hidden" animate="visible" custom={7} variants={fadeUp}>
        <blockquote className="font-playfair italic text-lg text-muted-foreground max-w-sm mx-auto">
          "Your unfiltered thought is worth more than your polished one."
        </blockquote>
      </motion.div>
    </div>
  );
};

export default WelcomeConfirmation;
